import React, { useMemo } from 'react';
import { Component, ComponentType, ComponentSeries, BUSINESS_UNITS, Roles } from '@cambridgeassessment/lib-ee-commons';
import { componentFileTypesOnly } from '../FileTypeComponent/fileTypes';
import { useDispatch, useSelector } from 'react-redux';
import * as thunks from '../../../../../store/components/thunks';
import { LocalSection } from '../../../../../service/components/types';
import { StyledToggleContainer } from '../styles';
import { brandedFC } from '../../../../../branding/brandedFC';
import { Brand } from '../../../../../branding/brand';
import SwitchComponent from './SwitchComponent';
import { Authorized } from 'lib-browser';
import { RootState } from '../../../../../store';

interface Props {
  compId: string;
  component: Component;
  switchChecked: boolean;
  deleteLocalSection: () => void;
  disabled?: boolean;
}

interface ExtendedProps extends Props {
  brand: Brand;
}

export const DigitallyReceivedControl: React.FC<ExtendedProps> = ({
  brand,
  compId,
  component,
  disabled,
  switchChecked,
  deleteLocalSection,
}) => {
  const dispatch = useDispatch();
  const selectedRole = useSelector((state: RootState) => state.userData.selectedRole);

  const isExamComponent = brand.allowExaminedComponents && component.isExam;
  const isCompConfirmed = component.toBeConfirmed;
  const isComponentModerated = component.type === ComponentType.MODERATED;
  const sectionData = {
    name: component.name,
    fileTypes: [...componentFileTypesOnly],
    allowMultipleFiles: component.businessUnit.id === BUSINESS_UNITS.OCR.id,
  };

  const handleExamComponent = (checked: boolean): void => {
    const defaultExamOffset = { date: 5, buffer: 14 };
    const defaultPoeOffset = { date: 0, buffer: 10 };

    const switchOnSeriesUpdate: ComponentSeries[] = [];
    component.series.forEach((item) => {
      switchOnSeriesUpdate.push({
        ...item,
        examDates: item.examDates.map((object) => ({
          ...object,
          submissionOffset: component.isPoE ? defaultPoeOffset : defaultExamOffset,
        })),
      });
    });

    const switchOffSeriesUpdate: ComponentSeries[] = [];
    component.series.forEach((item) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      switchOffSeriesUpdate.push({ ...item, examDates: item.examDates.map(({ submissionOffset, ...rest }) => rest) });
    });

    const updatedComponent: Component = {
      ...component,
      additionalFileTypes: checked ? [...componentFileTypesOnly] : [],
      series: checked ? switchOnSeriesUpdate : switchOffSeriesUpdate,
    };

    dispatch(thunks.updateAdditionalFileTypes(compId, updatedComponent.additionalFileTypes));
    dispatch(thunks.updateComponent(compId, updatedComponent, 'series'));

    if (checked) {
      dispatch(thunks.createDigitallyReceivedFlag(compId, sectionData as LocalSection));
      dispatch(thunks.updateSeriesWhenSwitchingToggleOn(compId));
    } else {
      dispatch(thunks.deleteDigitallyReceivedFlag(compId));
      deleteLocalSection();

      const updatedAdditionalWorkComponent: Component = { ...component, allowAdditionalWorkSubmission: false };

      dispatch(thunks.updateComponent(component._id, updatedAdditionalWorkComponent, 'allowAdditionalWorkSubmission'));
      if (component.componentSections.length) {
        dispatch(thunks.deleteComponentSections(compId));
      }
    }
  };

  const handleCourseworkComponent = async (checked: boolean): Promise<void> => {
    if (isComponentModerated) {
      const updatedComponent: Component = {
        ...component,
        receiveSamples: checked,
      };

      dispatch(thunks.updateComponent(compId, updatedComponent, 'receiveSamples'));
    }

    if (checked) {
      dispatch(thunks.updateAdditionalFileTypes(compId, [...componentFileTypesOnly]));
      dispatch(thunks.createDigitallyReceivedFlag(compId, sectionData as LocalSection));
      dispatch(thunks.updateSeriesWhenSwitchingToggleOn(compId));
    } else {
      dispatch(thunks.updateAdditionalFileTypes(compId, []));
      dispatch(thunks.deleteDigitallyReceivedFlag(compId));

      // eslint-disable-next-line @typescript-eslint/await-thenable
      await deleteLocalSection();

      const updatedAdditionalWorkComponent: Component = { ...component, allowAdditionalWorkSubmission: false };

      dispatch(thunks.updateComponent(component._id, updatedAdditionalWorkComponent, 'allowAdditionalWorkSubmission'));
      if (component.componentSections.length) {
        dispatch(thunks.deleteComponentSections(compId));
      }
    }
  };

  const handleReceiveMarksFlag = (checked: boolean): void => {
    const updatedComponent: Component = {
      ...component,
      receiveMarks: checked,
    };

    dispatch(thunks.updateComponent(compId, updatedComponent, 'receiveMarks'));

    if (checked) {
      dispatch(thunks.updateSeriesWhenSwitchingToggleOn(compId));
    }
  };

  const resolvedLabel = useMemo(() => {
    if (isComponentModerated) {
      return 'Receive samples';
    }
    return isExamComponent ? 'Receive exam digitally' : 'Receive work digitally';
  }, [isComponentModerated, isExamComponent]);

  return (
    <Authorized
      selectedRole={selectedRole}
      roles={[Roles.SUBMIT_SUPER_ADMIN]}
      readOnlyRoles={[Roles.SUBMIT_COMPLIANCE, Roles.SUBMIT_INTERNAL_ASSESSMENT_MANAGER, Roles.SUBMIT_ADMINISTRATOR]}
    >
      {isComponentModerated && (
        <StyledToggleContainer>
          <Authorized
            selectedRole={selectedRole}
            roles={[Roles.SUBMIT_SUPER_ADMIN]}
            readOnlyRoles={[
              Roles.SUBMIT_COMPLIANCE,
              Roles.SUBMIT_INTERNAL_ASSESSMENT_MANAGER,
              Roles.SUBMIT_ADMINISTRATOR,
            ]}
          >
            <SwitchComponent
              label={'Receive marks'}
              checked={!!component.receiveMarks}
              onChange={handleReceiveMarksFlag}
              dataQaId={`${component.name}-receive-marks`}
              readOnly={disabled}
            />
          </Authorized>
        </StyledToggleContainer>
      )}
      <SwitchComponent
        label={resolvedLabel}
        checked={switchChecked}
        onChange={isExamComponent ? handleExamComponent : handleCourseworkComponent}
        dataQaId={`${component.name}-receive-digitally`}
        readOnly={disabled || isCompConfirmed}
      />
    </Authorized>
  );
};

export default brandedFC<Props>((brand, props) => <DigitallyReceivedControl brand={brand} {...props} />);
